import React, {
  FunctionComponent,
  useState,
  useCallback,
  useEffect
} from 'react';
import Snackbar, { SnackbarOrigin } from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import PropTypes from 'prop-types';
import { clearSmartEntriesSelection } from 'modules/smartDiary/smartDiaryNotes/hooks';
import { recordMetric } from 'modules/metrics';
import { isWithinAnotherApp } from 'containerApp';
import { popAlerts, LoginAlert } from './loginAlerts';
import LoadingLoginUiCircular from './LoadingLoginUiCircular';
import LoginUi from './LoginUi';

const snackBarOrigin: SnackbarOrigin = {
  horizontal: 'center',
  vertical: 'bottom'
};

const LoginPage: FunctionComponent<{
  loginRedirect: () => void;
  checkIsValidSession: () => Promise<boolean | undefined>;
}> = ({
  loginRedirect,
  checkIsValidSession
}: {
  loginRedirect: () => void;
  checkIsValidSession: () => Promise<boolean | undefined>;
}) => {
  const [disable, setDisable] = useState(false);
  const onLogin = useCallback(async () => {
    setDisable(true);
    await recordMetric('login-click', 2000);
    clearSmartEntriesSelection();
    sessionStorage.clear();
    loginRedirect();
  }, [loginRedirect]);

  const [alerts, setAlerts] = useState<LoginAlert[]>([]);

  const [isValidSession, setIsValidSession] = useState<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    setAlerts(popAlerts());
  }, []);

  const dismissAlert = useCallback(
    (index) => () => {
      setAlerts(alerts.slice(index));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setAlerts]
  );

  useEffect(() => {
    (async function () {
      try {
        const isValidSession = await checkIsValidSession();
        setIsValidSession(isValidSession);
        const alerts = popAlerts();
        if (alerts.length) {
          setAlerts(alerts);
        }
      } catch (e) {
        setIsValidSession(false);
        console.error(e);
      }
    })();
  }, [checkIsValidSession]);

  useEffect(() => {
    sessionStorage.removeItem('ACTUALUSERURI');
    if (isValidSession) {
      onLogin();
    }
  }, [isValidSession, onLogin]);

  if (isValidSession === undefined) {
    return <LoadingLoginUiCircular />;
  }
  return (
    <div>
      <>
        <LoginUi disable={disable} onClick={onLogin} />
      </>
      {alerts.map((alert, index) => (
        <Snackbar
          key={index}
          open
          style={{ marginBottom: `${32 + 64 * index}px` }}
          onClose={alert.dismissible ? dismissAlert(index) : undefined}
          anchorOrigin={snackBarOrigin}
        >
          <MuiAlert
            onClose={alert.dismissible ? dismissAlert(index) : undefined}
            severity={alert.severity}
            aria-label={isWithinAnotherApp() ? alert.message : ''}
          >
            {alert.message}
          </MuiAlert>
        </Snackbar>
      ))}
    </div>
  );
};

LoginPage.propTypes = {
  loginRedirect: PropTypes.func.isRequired
};

export default LoginPage;
